'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useForm, RegisterOptions } from 'react-hook-form';
import { useSession } from 'next-auth/react';

import styles from './estimateShippingModal.module.scss';

import StylizedButton from '@/components/stylizedButton';
import StylizedSelect from '@/components/stylizedSelect';
import Loader from '@/components/loader';

import { checkShippingEligibilityForLocation } from '@/lib/functions/checkShippingEligibilityForLocation';
import { estimateOrderShipping } from '@/lib/functions/estimateOrderShipping';
import { logShippingEstimate } from '@/lib/functions/logShippingEstimate';
import { formatProvinceCode } from '@/lib/functions/formatProvinceCode';
import { resolveEstimateShippingModalDefaults } from '@/lib/functions/resolveCheckoutShippingDefaults';
import {
  useSaveShippingRulesToContext,
} from '@/lib/context/shippingEligibilityContext';

import { useOrder } from '@/lib/context/orderContext';

import {
  CANADIAN_PROVINCES,
  COUNTRY_CODES,
  DEFAULT_COUNTRY_CODE,
  getPostalCodeLabel,
  getPostalCodePattern,
  getProvinceLabel,
  isCanada,
  isProvinceRequired,
  isUnitedStates,
} from '@/lib/constants/countries';
import { STATES_CODES } from '@/lib/constants/common';

import { SyliusAddress } from '@/lib/types/SyliusAddress';

import type { OrderPayload } from '@/lib/types/orderPayload';

interface EstimateShippingModalProps {
  onClose: () => void;
  onSuccess: (order?: OrderPayload | null) => void;
  prefillFromProfile?: boolean;
}

type EstimateShippingInputs = {
  register_address: string;
  register_address2: string;
  register_city: string;
  register_country: string;
  register_state: string;
  register_zipcode: string;
};

const EstimateShippingModal: React.FC<EstimateShippingModalProps> = ({
  onClose,
  onSuccess,
  prefillFromProfile = false,
}) => {
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Checking shipping eligibility...');
  const [formError, setFormError] = useState<string | null>(null);
  const appliedProfileAddressRef = useRef(false);

  const { data: session, status } = useSession();
  const formDefaults = useMemo(
    () => (
      prefillFromProfile
        ? resolveEstimateShippingModalDefaults(session?.user?.shippingAddress)
        : resolveEstimateShippingModalDefaults(null)
    ),
    [prefillFromProfile, session?.user?.shippingAddress],
  );
  const hasProfileAddress = prefillFromProfile && Boolean(formDefaults.register_address);

  const [selectedCountry, setSelectedCountry] = useState(
    () => formDefaults.register_country || DEFAULT_COUNTRY_CODE,
  );

  const { state: orderState, dispatch: orderDispatch } = useOrder();
  const saveShippingRulesToContext = useSaveShippingRulesToContext();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    clearErrors,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<EstimateShippingInputs>({
    defaultValues: formDefaults,
    mode: 'onChange',
    shouldUnregister: true,
  });

  const watchedCountry = watch('register_country');
  const activeCountry = (watchedCountry || selectedCountry || DEFAULT_COUNTRY_CODE).toUpperCase();
  const selectedState = watch('register_state');

  const provinceValidation = useMemo<RegisterOptions<EstimateShippingInputs, 'register_state'>>(
    () => ({
      validate: (value, formValues) => {
        const country = String(formValues.register_country || DEFAULT_COUNTRY_CODE).toUpperCase();
        if (!isProvinceRequired(country)) {
          return true;
        }

        if (!String(value ?? '').trim()) {
          return `Please select a ${getProvinceLabel(country).toLowerCase()}.`;
        }

        return true;
      },
    }),
    [],
  );

  useEffect(() => {
    if (!prefillFromProfile || status !== 'authenticated' || !hasProfileAddress || appliedProfileAddressRef.current) {
      return;
    }

    appliedProfileAddressRef.current = true;
    reset(formDefaults);
    setSelectedCountry(formDefaults.register_country);
  }, [prefillFromProfile, status, hasProfileAddress, formDefaults, reset]);

  useEffect(() => {
    const normalizedCountry = activeCountry.toUpperCase();
    if (normalizedCountry !== selectedCountry) {
      setSelectedCountry(normalizedCountry);
    }
    if (!isProvinceRequired(normalizedCountry)) {
      clearErrors('register_state');
    }
  }, [activeCountry, clearErrors, selectedCountry]);

  const validationRules = useMemo(() => ({
    register_address: {
      required: 'Address line 1 is required.',
      pattern: {
        value: /\S+/,
        message: 'Please enter a valid address line 1.',
      },
    },
    register_city: {
      required: 'City / Locality is required.',
      pattern: {
        value: /\S+/,
        message: 'Please enter a valid city or locality.',
      },
    },
    register_state: provinceValidation,
    register_zipcode: {
      required: `${getPostalCodeLabel(activeCountry)} is required.`,
      pattern: {
        value: getPostalCodePattern(activeCountry),
        message: `Please enter a valid ${getPostalCodeLabel(activeCountry).toLowerCase()}.`,
      },
    },
  }), [activeCountry, provinceValidation]);

  const provinceOptions = isUnitedStates(activeCountry)
    ? STATES_CODES
    : isCanada(activeCountry)
      ? CANADIAN_PROVINCES
      : [];

  const renderFormField = (
    id: keyof EstimateShippingInputs,
    label: string,
    errorMessage: string,
    registerOptions: RegisterOptions<EstimateShippingInputs, keyof EstimateShippingInputs> = {},
    type: string = 'text',
    autoComplete: string = '',
    fieldClassName?: string,
    inputClassName?: string,
  ) => (
    <div className={classNames('formInput', 'formText', styles.formField, fieldClassName)} key={id}>
      <label htmlFor={id}>
        {label}
        {registerOptions?.required && <span className={styles.required}>*</span>}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        aria-invalid={!!errors[id]}
        aria-describedby={showErrors && errors[id] ? `${id}-error` : undefined}
        disabled={loading}
        className={inputClassName}
        {...register(id, registerOptions)}
      />
      {showErrors && errors[id] && (
        <span id={`${id}-error`} className={classNames(styles.error, styles.fontColor_error)}>
          {(errors[id]?.message as string) || errorMessage}
        </span>
      )}
    </div>
  );

  const handleCountryChange = (value: string | number) => {
    const countryCode = String(value).toUpperCase();
    setSelectedCountry(countryCode);
    setValue('register_country', countryCode, { shouldValidate: true, shouldDirty: true });
    setValue('register_state', isUnitedStates(countryCode) ? 'TX' : '', { shouldValidate: true, shouldDirty: true });
    setValue('register_zipcode', '', { shouldValidate: true, shouldDirty: true });
    if (!isProvinceRequired(countryCode)) {
      clearErrors('register_state');
    }
    void trigger('register_state');
  };

  const onSubmit = async (formData: EstimateShippingInputs) => {
    setShowErrors(true);
    setFormError(null);
    setLoading(true);
    setLoadingMessage('Checking shipping eligibility...');

    try {
      const countryCode = (formData.register_country || activeCountry).toUpperCase();
      const eligibility = await checkShippingEligibilityForLocation(
        formData.register_state,
        formData.register_zipcode,
        countryCode,
      );

      if (!eligibility.eligible) {
        setFormError(eligibility.message);
        return;
      }

      if (eligibility.processedRules) {
        saveShippingRulesToContext(eligibility.processedRules);
      }

      setLoadingMessage('Updating your shipping address...');

      const shippingAddress: SyliusAddress = {
        firstName: 'Guest',
        lastName: 'Customer',
        street: formData.register_address,
        street2: formData.register_address2 || '',
        city: formData.register_city,
        postcode: formData.register_zipcode,
        countryCode,
        provinceCode: formatProvinceCode(countryCode, formData.register_state),
        phoneNumber: '5555555555',
        email: '',
      };

      const result = await estimateOrderShipping(
        orderState,
        orderDispatch,
        shippingAddress,
      );

      logShippingEstimate('modal_result', {
        success: result.success,
        failureStep: result.failureStep ?? null,
        message: result.message || null,
      });

      if (!result.success) {
        setFormError(result.message);
        return;
      }

      onSuccess(result.order ?? null);
    } catch (error) {
      logShippingEstimate('modal_exception', {
        message: error instanceof Error ? error.message : 'unknown',
      });
      setFormError('Something went wrong while checking your address. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.estimateShipping}>
      <h3 className={styles.estimateShippingTitle}>Enter your address</h3>
      <p>To confirm shipping eligibility, please enter your address below.</p>
      {loading ? (
        <Loader variant="2" message={loadingMessage} />
      ) : (
        // Enter submits the pseudo-form without nesting a <form> inside checkout.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          role="group"
          aria-label="Estimate shipping address"
          className={styles.estimateShippingForm}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
              e.preventDefault();
              handleSubmit(onSubmit, () => setShowErrors(true))();
            }
          }}
        >
          <div className={classNames(styles.row, styles.countryRow)}>
            <div className={classNames(styles.selectWrapper, styles.countryWrapper)}>
              <StylizedSelect
                key={`country-${activeCountry}`}
                label="Country / Region"
                variantLabel="inline"
                placeHolder="Select a country"
                options={COUNTRY_CODES}
                styles={{
                  selectWrapper: styles.countrySelectWrapper,
                  container: styles.countrySelectContainer,
                  control: styles.countrySelectControl,
                }}
                onChange={handleCountryChange}
                defaultValue={activeCountry}
                size="large"
                required
              />
              <input
                id="country-select-hidden"
                type="hidden"
                {...register('register_country', { required: 'Country is required.' })}
              />
            </div>
          </div>

          <div className={styles.row}>
            {renderFormField(
              'register_address',
              'Address line 1',
              validationRules.register_address.pattern!.message!,
              validationRules.register_address,
              'text',
              'address-line1',
            )}

            {renderFormField(
              'register_address2',
              'Address line 2 (Apt, suite, unit)',
              '',
              {},
              'text',
              'address-line2',
            )}
          </div>

          <div className={classNames(styles.row, styles.cityRow)}>
            {renderFormField(
              'register_city',
              'City / Locality',
              validationRules.register_city.pattern!.message!,
              validationRules.register_city,
              'text',
              'address-level2',
            )}

            {provinceOptions.length > 0 ? (
              <div className={classNames(styles.selectWrapper, styles.provinceWrapper)} key={`province-select-${activeCountry}`}>
                <StylizedSelect
                  key={`state-${activeCountry}-${selectedState}`}
                  label={isUnitedStates(activeCountry) ? 'State' : isCanada(activeCountry) ? 'Province' : 'State / Province / Region'}
                  variantLabel="inline"
                  placeHolder={`Select a ${(isUnitedStates(activeCountry) ? 'state' : isCanada(activeCountry) ? 'province' : 'state / province / region')}`}
                  options={provinceOptions}
                  onChange={(value) => {
                    setValue('register_state', value as string, { shouldValidate: true });
                  }}
                  defaultValue={selectedState || (isUnitedStates(activeCountry) ? 'TX' : '')}
                  size="large"
                  classname="province"
                  required={isProvinceRequired(activeCountry)}
                />
                <input
                  id="state-select-hidden"
                  type="hidden"
                  aria-invalid={!!errors.register_state}
                  aria-describedby={(showErrors || isSubmitted) && errors.register_state ? 'register_state-error' : undefined}
                  {...register('register_state', provinceValidation)}
                />
                {(showErrors || isSubmitted) && errors.register_state && (
                  <span id="register_state-error" className={classNames(styles.error, styles.fontColor_error)}>
                    {errors.register_state.message as string}
                  </span>
                )}
              </div>
            ) : (
              <div key={`province-text-${activeCountry}`}>
                {renderFormField(
                  'register_state',
                  'State / Province / Region',
                  'Please enter a valid state or province.',
                  provinceValidation,
                  'text',
                  'address-level1',
                )}
              </div>
            )}

            {renderFormField(
              'register_zipcode',
              isUnitedStates(activeCountry) ? 'ZIP code' : 'Postal code',
              validationRules.register_zipcode.pattern!.message!,
              validationRules.register_zipcode,
              'text',
              'postal-code',
              styles.zipcode,
              styles.zipcodeInput,
            )}
          </div>

          {formError && (
            <p className={classNames(styles.error, styles.fontColor_error)} role="alert">
              {formError}
            </p>
          )}

          <div className={classNames(styles.buttonRow, styles.buttonRow)}>
            <StylizedButton
              type="button"
              variant="primary"
              className={styles.cancelButton}
              onClick={() => {
                if (!loading) onClose();
              }}
            >
              Cancel
            </StylizedButton>
            <StylizedButton
              variant="primary"
              onClick={handleSubmit(onSubmit, () => setShowErrors(true))}
            >
              Check Address
            </StylizedButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateShippingModal;
